import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6e2c-3 — ACCESSORY SELECTOR data layer (§33.8 — damper/louver/hood/
 * opening, accessory.selector.js 1,913).
 *
 * The shared <AccessorySelector> reuses the COMPONENT-FORM GET endpoints for
 * read-only loads (staging snapshot = GET ahu/<plural>/<id> via
 * getComponentDetail). This file adds the selector-specific data calls:
 *
 *  - getAccessoryParamsDoc / getMasterListRaw / getCatalogPageRaw /
 *    getCatalogCountRaw — read-only GETs (CMS params merges, louver blade
 *    orientations, smartlist pages + counts). All keep raw URLs (legacy
 *    fn.uni_tools.get) so the per-field damper CMS (10 parallel
 *    `dampers/params/?<field>=true`) and the fan-intake / hood-constrained
 *    swaps map directly onto RTKQ cache keys (the legacy `dampers_cms`
 *    stateStorage + module-level pending dedup → RTKQ URL cache + in-flight
 *    dedup, identical semantics).
 *
 *  - patchAccessoryWithPosition — THE staging round-trip (getAccessoryData):
 *    PATCH ahu/<type>s/<id>/with_position. ⚠ DATA-SAFETY: per the backend
 *    with_position contract (#55 / Q16) the server SAVES (not just validates).
 *    This mutation WRITES to the shared dev3 DB. The selector fires it ONLY on a
 *    real user field/position change (userArmed gate — nothing on open / mount /
 *    tab-switch / cancel-without-edits). In smoke testing the round-trip is
 *    exercised by CODE REVIEW only, never against live data (PARKED for human QA
 *    on throwaway data). Body shape is byte-for-byte the legacy getAccessoryData
 *    payload (see AccessorySelector.buildWithPositionBody).
 */

export type IAccessoryStagingRow = Record<string, unknown>;
export type IAccessoryParamsDoc = Record<string, unknown>;
export type ISmartlistItem = { id: number } & Record<string, unknown>;

export interface WithPositionBody {
  [accessoryKey: string]: Record<string, unknown>;
  position: Record<string, unknown>;
}

export interface WithPositionResponse {
  position?: Record<string, unknown>;
  [accessoryKey: string]: Record<string, unknown> | undefined;
}

export const accessorySelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- read-only GETs (legacy fn.uni_tools.get; RTKQ caches by URL) ----

    /** A single CMS params doc by full URL (opening/hood/louver single doc;
     * damper per-field `?<field>=true`; fan-intake / hood-constrained swaps). */
    getAccessoryParamsDoc: builder.query<IAccessoryParamsDoc, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    /** Louver blade orientations (master/v1.0.0/ahu/louver_blade_orientations
     * ?mfg_id&series_id). Returns the array (or {results:[]}). */
    getLouverBladeOrientations: builder.query<
      Array<{ id: number; name: string } & Record<string, unknown>>,
      string
    >({
      query: (url) => url,
      transformResponse: (raw: unknown) =>
        Array.isArray(raw)
          ? (raw as Array<{ id: number; name: string }>)
          : (((raw as { results?: unknown })?.results as Array<{ id: number; name: string }>) ?? []),
    }),

    /** Smartlist page: <base>?<params>&page=N (50/page). */
    getSmartlistPage: builder.query<ISmartlistItem[], string>({
      query: (url) => url,
      transformResponse: (raw: unknown) => (Array.isArray(raw) ? (raw as ISmartlistItem[]) : []),
    }),

    /** Combo Louver (KA-5245) — A1.1: the Enclosure Material option list.
     * `_fetchEnclosureMaterials` :1874-1908 →
     * GET /api/v1.0/ahu/louvers/<id>/enclosure_materials/ →
     * {"materials":[{id,name} × 9],"default":697} (inventory §3.1 R3). These are
     * the AHU's casing sheet metals (CMSAHUCasingMetalSheet by mfg +
     * construction class — CMSAHUSheetMetal is empty), same source as the cabinet
     * outer skin. Legacy dedups the in-flight promise via stateStorage.models;
     * RTKQ's URL cache + in-flight dedup is the identical semantic. */
    getEnclosureMaterials: builder.query<
      { materials: Array<{ id: number; name: string }>; default: number | null },
      number
    >({
      query: (louverId) => `/api/v1.0/ahu/louvers/${louverId}/enclosure_materials/`,
      transformResponse: (raw: unknown) => {
        // legacy shape tolerance (:1890-1891): a bare array, {materials}, or
        // {results}.
        const bag = raw as
          | { materials?: unknown; results?: unknown; default?: unknown }
          | undefined;
        const materials = Array.isArray(raw)
          ? (raw as Array<{ id: number; name: string }>)
          : ((bag?.materials ?? bag?.results ?? []) as Array<{ id: number; name: string }>);
        const def = bag && !Array.isArray(raw) ? bag.default : null;
        return { materials, default: typeof def === 'number' ? def : null };
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    /** Smartlist count: <base>count/?<params> (filtered) or <base>count/
     * (unfiltered total). The endpoint returns {count:N}. */
    getSmartlistCount: builder.query<number | null, string>({
      query: (url) => url,
      transformResponse: (raw: unknown) => {
        const c = (raw as { count?: unknown })?.count;
        return typeof c === 'number' ? c : null;
      },
    }),

    // ---- THE staging round-trip (getAccessoryData) ⚠ WRITES dev3 ----
    // PATCH ahu/<type>s/<id>/with_position. userArmed-gated by the selector;
    // NEVER auto-fired (data-safety §4 / 6e2a userArmed pattern).
    patchAccessoryWithPosition: builder.mutation<
      WithPositionResponse,
      { plural: string; id: number; body: WithPositionBody }
    >({
      query: ({ plural, id, body }) => ({
        url: `/api/v1.0/ahu/${plural}/${id}/with_position`,
        method: 'PATCH',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetAccessoryParamsDocQuery,
  useLazyGetLouverBladeOrientationsQuery,
  useLazyGetSmartlistPageQuery,
  useLazyGetSmartlistCountQuery,
  useLazyGetEnclosureMaterialsQuery,
  usePatchAccessoryWithPositionMutation,
} = accessorySelectorApi;

/** Damper CMS: 10 parallel `dampers/params/?<field>=true` merged into one doc
 * (fn['current.cms.damper'] / map.js:430). Returns the merged `.damper` object.
 * Reuses the `getAccessoryParamsDoc` cache per field URL. */
export const DAMPER_CMS_FIELDS = [
  'mfg',
  'series',
  'leakage',
  'blade_orientation',
  'frame_mtl',
  'shaft_mtl',
  'width',
  'height',
  'mounting',
  'manufacturer_series',
] as const;

export const CMS_URL_FOR_TYPE: Record<string, string> = {
  opening: '/api/v1.0/cms/1/v1.0.0/ahu/opening/params/',
  hood: '/api/v1.0/cms/1/v1.0.0/ahu/hoods/params/',
  louver: '/api/v1.0/cms/1/v1.0.0/ahu/louvers/params/',
  // damper resolves per-field below (no single URL).
};

/** Unwrap the {"<type>": {...}} CMS doc wrapper (mirrors useAccessoryForm). */
export function unwrapAccessoryCms(
  raw: Record<string, unknown> | undefined,
  key: string
): Record<string, unknown> {
  if (!raw) return {};
  const inner = raw[key];
  if (inner && typeof inner === 'object') return inner as Record<string, unknown>;
  const keys = Object.keys(raw);
  if (keys.length === 1 && raw[keys[0]] && typeof raw[keys[0]] === 'object') {
    return raw[keys[0]] as Record<string, unknown>;
  }
  return raw;
}
